import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { testDatabaseConnection } from '../lib/database';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [member, setMember] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Initialize authentication state from Supabase
  useEffect(() => {
    const initAuth = async () => {
      // Test database connection first
      const dbConnected = await testDatabaseConnection();
      if (!dbConnected) {
        console.warn('Database connection failed. Some features may not work.');
      }

      // Get initial session
      const { data: { session } } = await supabase.auth.getSession();
      setUser(session?.user ?? null);
      if (session?.user) {
        await fetchMemberData(session.user.id);
      }
      setIsLoading(false);
    };

    initAuth();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('Auth state changed:', event, session?.user?.email);
        
        if (event === 'SIGNED_OUT') {
          console.log('User signed out, clearing state');
          setUser(null);
          setMember(null);
          setIsLoading(false);
          return;
        }
        
        if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
          console.log('User signed in/token refreshed');
          setUser(session?.user ?? null);
          
          // Set loading to false immediately for user authentication
          console.log('Setting isLoading to false for user authentication');
          setIsLoading(false);
          
          if (session?.user) {
            console.log('Fetching member data for user:', session.user.id);
            
            // Fetch member data in background without blocking UI
            fetchMemberData(session.user.id).then(async (memberData) => {
              // If no member data found and this is a fresh sign-in (not token refresh)
              if (!memberData && event === 'SIGNED_IN') {
                console.log('No member profile found for new user, checking if we need to create one');
                
                // Check if this user has pending profile data (from signup with email confirmation)
                const pendingData = localStorage.getItem(`pendingProfile_${session.user.id}`);
                if (pendingData) {
                  try {
                    const userData = JSON.parse(pendingData);
                    console.log('Found pending profile data, creating member profile:', userData);

                    const profileResult = await createMemberProfile(userData, session.user);
                    if (profileResult.success) {
                      console.log('Member profile created after email confirmation');
                      localStorage.removeItem(`pendingProfile_${session.user.id}`);

                      // Mark invite as accepted if this signup came from an invite
                      if (userData.inviteId) {
                        await supabase
                          .from('invites')
                          .update({ status: 'accepted' })
                          .eq('id', userData.inviteId);
                      }
                    }
                  } catch (error) {
                    console.error('Error creating profile from pending data:', error);
                  }
                }
              }
            }).catch(error => {
              console.error('Background member data fetch failed:', error);
            });
          }
          
          return;
        }
        
        // For other events, just update the user
        setUser(session?.user ?? null);
        if (!session?.user) {
          setMember(null);
        }
        console.log('Setting isLoading to false for other events');
        setIsLoading(false);
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  const fetchMemberData = async (userId) => {
    console.log('fetchMemberData called with userId:', userId);
    
    // Create a timeout promise
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Member data fetch timeout')), 10000); // 10 second timeout
    });
    
    try {
      // Race between the actual query and timeout
      const queryPromise = supabase
        .from('members')
        .select(`
          *,
          member_roles (
            *,
            roles (*)
          )
        `)
        .eq('supabase_user_id', userId)
        .single();
      
      const { data, error } = await Promise.race([queryPromise, timeoutPromise]);
      
      console.log('Member query result:', { data, error });

      if (data) {
        console.log('Member data found:', data);
        setMember(data);
        return data; // Return the member data
      } else if (error?.code === 'PGRST116') {
        // Member not found, this is okay - user exists but no member profile yet
        console.log('No member profile found for user:', userId);
        setMember(null);
        return null;
      } else if (error) {
        console.error('Error fetching member data:', error);
        setMember(null);
        return null;
      }
    } catch (error) {
      console.error('Caught error in fetchMemberData:', error);
      setMember(null);
      return null;
    } finally {
      // Ensure this function always completes
      console.log('fetchMemberData completed for user:', userId);
    }
    
    return null;
  };

  const login = async (email, password) => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        console.error('Login error:', error);
        
        // Handle specific error cases
        if (error.message.includes('Email not confirmed')) {
          return { 
            success: false, 
            error: 'Please check your email and confirm your account before signing in.' 
          };
        } else if (error.message.includes('Invalid login credentials')) {
          return { 
            success: false, 
            error: 'Invalid email or password. If you don\'t have an account yet, use "Council Registration" to create one.' 
          };
        } else {
          return { success: false, error: error.message };
        }
      }

      return { success: true, user: data.user };
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: 'Login failed. Please try again.' };
    } finally {
      setIsLoading(false);
    }
  };

  const signup = async (email, password, userData = {}) => {
    console.log('Signup called with:', { email, userData });
    setIsLoading(true);
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: userData, // This will be available in the user metadata
          emailRedirectTo: `${window.location.origin}/dashboard`
        }
      });
      
      console.log('Supabase signup result:', { data, error });

      if (error) {
        console.error('Signup error:', error);
        return { success: false, error: error.message };
      }

      // Check if user already exists
      if (data.user && data.user.identities && data.user.identities.length === 0) {
        return { success: false, error: 'User already registered with this email' };
      }

      // If we have a session immediately, email confirmation is disabled
      if (data.session) {
        console.log('User signup successful, creating member profile...');
        
        // Try to create member profile automatically
        const profileResult = await createMemberProfile({
          firstName: userData.firstName || '',
          lastName: userData.lastName || '',
          studentId: userData.studentId || '',
          program: userData.program || '',
          graduationYear: userData.graduationYear ? parseInt(userData.graduationYear) : null,
          role: userData.role
        }, data.user); // Pass the new user object

        if (!profileResult.success) {
          console.error('Failed to create member profile:', profileResult.error);
          // Don't fail the signup, but return the error for handling
          return { 
            success: true, 
            user: data.user,
            needsEmailConfirmation: false,
            profileCreated: false,
            profileError: profileResult.error
          };
        }

        console.log('Member profile created successfully during signup');
        return { 
          success: true, 
          user: data.user,
          needsEmailConfirmation: false,
          profileCreated: true,
          member: profileResult.member
        };
      } else {
        // Email confirmation is required - store userData for later profile creation
        console.log('Email confirmation required, storing profile data for after confirmation');
        
        // Store pending profile data in localStorage for after email confirmation
        if (data.user?.id) {
          localStorage.setItem(`pendingProfile_${data.user.id}`, JSON.stringify(userData));
        }
        
        return { 
          success: true, 
          user: data.user,
          needsEmailConfirmation: true,
          pendingProfileData: userData
        };
      }
    } catch (error) {
      console.error('Signup error:', error);
      return { success: false, error: 'Signup failed. Please try again.' };
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      console.log('Logging out...');
      
      // Sign out from Supabase
      const { error } = await supabase.auth.signOut();
      if (error) {
        console.error('Error logging out:', error);
        throw error;
      }
      
      // Note: Don't manually set state here as the auth state listener will handle it
      console.log('Logout successful');
      return { success: true };
    } catch (error) {
      console.error('Logout failed:', error);
      return { success: false, error: error.message };
    }
  };

  const resendConfirmation = async (email) => {
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: email,
        options: {
          emailRedirectTo: `${window.location.origin}/dashboard`
        }
      });

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error) {
      console.error('Resend confirmation error:', error);
      return { success: false, error: 'Failed to resend confirmation email.' };
    }
  };

  const createMemberProfile = async (userData, targetUser = null) => {
    // Use passed user or current user
    const currentUser = targetUser || user;
    
    if (!currentUser) {
      console.error('No authenticated user for profile creation');
      return { success: false, error: 'No authenticated user' };
    }

    console.log('Creating member profile for user:', currentUser.id, userData);

    try {
      // First, check if profile already exists
      const { data: existingMember } = await supabase
        .from('members')
        .select('id, email, first_name, last_name')
        .eq('supabase_user_id', currentUser.id)
        .single();

      if (existingMember) {
        console.log('Member profile already exists:', existingMember);
        // Refresh member data to ensure context is updated
        await fetchMemberData(currentUser.id);
        return { success: true, member: existingMember };
      }

      // Prepare member data according to database schema
      const memberData = {
        supabase_user_id: currentUser.id,
        email: currentUser.email,
        first_name: userData.firstName || '',
        last_name: userData.lastName || '',
        student_id: userData.studentId || null,
        program: userData.program || null,
        graduation_year: userData.graduationYear || null,
        bio: userData.bio || null,
        membership_status: 'active', // Set as active by default
        // Set default privacy settings
        privacy_settings: {
          showEmail: false,
          showPhone: false,
          showSocial: true
        }
      };

      console.log('Inserting member data:', memberData);

      // Create new member profile
      const { data, error } = await supabase
        .from('members')
        .insert([memberData])
        .select()
        .single();

      if (error) {
        console.error('Error creating member profile:', error);
        return { success: false, error: error.message };
      }

      console.log('Member profile created successfully:', data);

      // Assign role based on userData.role or default to executive
      const roleName = userData.role || 'executive'; // Default to executive for council members
      console.log('Assigning role:', roleName, 'to member:', data.id);
      
      const roleResult = await assignRoleToMember(data.id, roleName);
      if (!roleResult) {
        console.warn('Failed to assign role:', roleName);
        // Still return success since the member was created
      }
      
      // Refresh member data to include role information
      await fetchMemberData(currentUser.id);
      
      return { success: true, member: data };
    } catch (error) {
      console.error('Failed to create profile:', error);
      return { success: false, error: 'Failed to create profile: ' + error.message };
    }
  };

  const assignDefaultRole = async (memberId) => {
    try {
      console.log('Assigning default role to member:', memberId);
      
      // Get the default member role
      const { data: role, error: roleError } = await supabase
        .from('roles')
        .select('id')
        .eq('name', 'member')
        .single();

      if (roleError) {
        console.error('Error fetching member role:', roleError);
        return false;
      }

      if (role) {
        const { error: insertError } = await supabase
          .from('member_roles')
          .insert([
            {
              member_id: memberId,
              role_id: role.id,
              is_primary: true
            }
          ]);

        if (insertError) {
          console.error('Error inserting member role:', insertError);
          return false;
        }

        console.log('Default role assigned successfully');
        return true;
      }
      
      console.warn('No member role found in database');
      return false;
    } catch (error) {
      console.error('Error assigning default role:', error);
      return false;
    }
  };

  const assignRoleToMember = async (memberId, roleName) => {
    try {
      console.log('Assigning role to member:', memberId, 'Original role:', roleName);
      
      // Map council positions to database roles
      const roleMapping = {
        'president': 'admin',
        'vice-president': 'executive', 
        'secretary': 'executive',
        'treasurer': 'executive',
        'academic-director': 'executive',
        'events-director': 'executive',
        'marketing-director': 'executive',
        'tech-director': 'executive',
        'external-relations': 'executive'
      };

      const dbRoleName = roleMapping[roleName] || 'executive';
      console.log('Mapped to database role:', dbRoleName);
      
      // Get the role from database
      const { data: role, error: roleError } = await supabase
        .from('roles')
        .select('id, name, display_name')
        .eq('name', dbRoleName)
        .single();

      console.log('Role query result:', { role, roleError });

      if (roleError) {
        console.error('Error fetching role:', roleError);
        console.error('Available roles check needed - role may not exist in database');
        return false;
      }

      if (!role) {
        console.error('Role not found in database:', dbRoleName);
        return false;
      }

      // Check if role assignment already exists
      const { data: existingRole, error: checkError } = await supabase
        .from('member_roles')
        .select('id')
        .eq('member_id', memberId)
        .eq('role_id', role.id)
        .eq('end_date', null) // Only active roles
        .single();

      if (existingRole) {
        console.log('Role already assigned to member');
        return true;
      }

      // Add role-specific title based on original council position
      const titleMapping = {
        'president': 'President',
        'vice-president': 'Vice President', 
        'secretary': 'Secretary',
        'treasurer': 'Treasurer',
        'academic-director': 'Academic Director',
        'events-director': 'Events Director',
        'marketing-director': 'Marketing Director',
        'tech-director': 'Technology Director',
        'external-relations': 'External Relations Director'
      };

      const roleTitle = titleMapping[roleName] || 'Executive Member';
      
      // Insert the role assignment
      const { data: insertedRole, error: insertError } = await supabase
        .from('member_roles')
        .insert([
          {
            member_id: memberId,
            role_id: role.id,
            title: roleTitle,
            department: 'Executive',
            is_primary: true,
            start_date: new Date().toISOString().split('T')[0] // Today's date
          }
        ])
        .select();

      if (insertError) {
        console.error('Error inserting member role:', insertError);
        return false;
      }

      console.log('Role assigned successfully:', insertedRole);
      console.log('Mapped:', roleName, '→', dbRoleName, 'with title:', roleTitle);
      return true;

    } catch (error) {
      console.error('Error in assignRoleToMember:', error);
      return false;
    }
  };

  // Send an invite to an email address (admin only)
  const sendInvite = async (email, role) => {
    if (!member) return { success: false, error: 'Not authenticated' };
    try {
      const { data: invite, error } = await supabase
        .from('invites')
        .insert([{ email: email.toLowerCase().trim(), invited_by: member.id, role }])
        .select()
        .single();

      if (error) return { success: false, error: error.message };

      const siteUrl = process.env.REACT_APP_SITE_URL || window.location.origin;
      const inviteLink = `${siteUrl}/login?invite=${invite.token}`;
      return { success: true, invite, inviteLink };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  // Validate an invite token (works for unauthenticated users)
  const validateInvite = async (token) => {
    try {
      const { data, error } = await supabase
        .from('invites')
        .select('*')
        .eq('token', token)
        .single();

      if (error || !data) return { valid: false, error: 'Invalid or expired invite link.' };
      if (data.status === 'accepted') return { valid: false, error: 'This invite has already been used.' };
      if (data.status === 'expired' || new Date(data.expires_at) < new Date()) {
        await supabase.from('invites').update({ status: 'expired' }).eq('id', data.id);
        return { valid: false, error: 'This invite has expired. Ask an admin for a new one.' };
      }

      return { valid: true, invite: data };
    } catch (error) {
      return { valid: false, error: 'Failed to validate invite.' };
    }
  };

  // Sign up using a valid invite token
  const signupWithInvite = async (token, email, password, userData = {}) => {
    const validation = await validateInvite(token);
    if (!validation.valid) return { success: false, error: validation.error };

    const invite = validation.invite;
    if (invite.email.toLowerCase() !== email.toLowerCase().trim()) {
      return { success: false, error: 'Email does not match the invite.' };
    }

    const result = await signup(email, password, { ...userData, role: invite.role });

    if (result.success) {
      if (!result.needsEmailConfirmation) {
        // Mark invite as accepted immediately
        await supabase.from('invites').update({ status: 'accepted' }).eq('id', invite.id);
      } else if (result.user?.id) {
        // Store inviteId so it gets marked accepted after email confirmation
        const existing = localStorage.getItem(`pendingProfile_${result.user.id}`);
        const pendingData = existing ? JSON.parse(existing) : {};
        localStorage.setItem(`pendingProfile_${result.user.id}`, JSON.stringify({
          ...pendingData,
          ...userData,
          role: invite.role,
          inviteId: invite.id
        }));
      }
    }

    return result;
  };

  // Resign a user — deactivates their account and ends all active roles
  const resignUser = async (memberId) => {
    try {
      const { error: statusError } = await supabase
        .from('members')
        .update({ membership_status: 'inactive' })
        .eq('id', memberId);

      if (statusError) return { success: false, error: statusError.message };

      const { error: rolesError } = await supabase
        .from('member_roles')
        .update({ end_date: new Date().toISOString().split('T')[0] })
        .eq('member_id', memberId)
        .is('end_date', null);

      if (rolesError) return { success: false, error: rolesError.message };

      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  // Fully delete a member account (calls a SECURITY DEFINER RPC to also delete the auth user)
  const deleteUser = async (memberId) => {
    try {
      const { error } = await supabase.rpc('delete_member_account', {
        target_member_id: memberId
      });
      if (error) return { success: false, error: error.message };
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  // Role-based permission checks
  const hasPermission = (requiredRole) => {
    // If user is authenticated but member is null, allow basic member access
    // This handles the case where user exists but member profile hasn't been created yet
    if (user && !member && requiredRole === 'member') {
      return true;
    }
    
    if (!member?.member_roles) return false;
    
    const roleHierarchy = {
      'member': 1,
      'executive': 2,
      'admin': 3
    };
    
    const userMaxLevel = Math.max(
      ...member.member_roles
        .filter(mr => !mr.end_date)
        .map(mr => roleHierarchy[mr.roles.name] || 0)
    );
    
    const requiredLevel = roleHierarchy[requiredRole] || 0;
    
    return userMaxLevel >= requiredLevel;
  };

  const canEdit = (resourceType) => {
    if (!member) return false;
    
    switch (resourceType) {
      case 'events':
        return hasPermission('executive');
      case 'team':
        return hasPermission('admin');
      case 'articles':
        return hasPermission('executive');
      case 'users':
        return hasPermission('admin');
      default:
        return false;
    }
  };

  const value = {
    user,
    member,
    isLoading,
    login,
    signup,
    logout,
    resendConfirmation,
    createMemberProfile,
    hasPermission,
    canEdit,
    sendInvite,
    validateInvite,
    signupWithInvite,
    resignUser,
    deleteUser,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};